/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import {
  get,
  ref,
  serverTimestamp,
  set,
  update,
  type Database,
} from "firebase/database";
import type { User } from "firebase/auth";
import { FALLBACK_DISPLAY_NAME } from "./username";

export interface UserProfile {
  displayName: string;
  photoURL: string | null;
  email: string;
  username?: string;
  usernameLower?: string;
  createdAt?: number | object;
}

export interface EnsureProfileResult {
  profile: UserProfile;
  needsUsername: boolean;
}

/**
 * Create the /users/{uid}/profile node on first auth, or backfill missing
 * scalar fields on legacy accounts (notably `createdAt`, which earlier builds
 * did not write). Does NOT auto-generate a username — the explicit onboarding
 * sheet is responsible for that so the user picks the handle themselves.
 *
 * Returns the resolved profile plus a `needsUsername` flag used by
 * AuthContext to surface the setup sheet.
 */
export async function ensureUserProfile(
  database: Database,
  user: Pick<User, "uid" | "displayName" | "photoURL" | "email">,
): Promise<EnsureProfileResult> {
  const profileRef = ref(database, `users/${user.uid}/profile`);
  const snap = await get(profileRef);

  if (!snap.exists()) {
    const fresh: UserProfile = {
      displayName: user.displayName ?? FALLBACK_DISPLAY_NAME,
      photoURL: user.photoURL ?? null,
      email: user.email ?? "",
      createdAt: serverTimestamp(),
    };
    await set(profileRef, fresh);
    return { profile: fresh, needsUsername: true };
  }

  const existing = snap.val() as Partial<UserProfile>;
  const patch: Record<string, unknown> = {};

  if (existing.createdAt === undefined) {
    patch.createdAt = serverTimestamp();
  }
  if (existing.email === undefined || existing.email === null) {
    patch.email = user.email ?? "";
  }
  if (existing.displayName === undefined || existing.displayName === null) {
    patch.displayName = user.displayName ?? FALLBACK_DISPLAY_NAME;
  }
  if (existing.photoURL === undefined) {
    patch.photoURL = user.photoURL ?? null;
  }

  if (Object.keys(patch).length > 0) {
    await update(profileRef, patch);
  }

  const profile: UserProfile = {
    displayName: (patch.displayName as string | undefined) ?? (existing.displayName ?? FALLBACK_DISPLAY_NAME),
    photoURL:
      patch.photoURL !== undefined
        ? (patch.photoURL as string | null)
        : existing.photoURL ?? null,
    email: (patch.email as string | undefined) ?? (existing.email ?? ""),
    username: existing.username,
    usernameLower: existing.usernameLower,
    createdAt: existing.createdAt ?? (patch.createdAt as object | undefined),
  };

  return {
    profile,
    needsUsername: existing.username === undefined,
  };
}
